import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { previewPlayerService } from '../services/PreviewPlayerService';
import './PreviewPlayer.css';

/**
 * GlobalPreviewPlayer - Optimized React wrapper for singleton PreviewPlayerService
 *
 * This component uses the global PreviewPlayerService to avoid destroying
 * HLS instances between previews, with optimized React performance.
 *
 * Quality modes:
 * - 'thumbnail': Forces lowest quality for hover previews (default)
 * - 'pdp': Allows normal quality for PDP modal previews
 *
 * Performance optimizations:
 * - Memoized callbacks to prevent unnecessary re-renders
 * - Reduced state updates
 * - Optimized effect dependencies
 * - Faster video element attachment
 */
const GlobalPreviewPlayer = ({
  hlsUrl,
  autoPlay = false,
  muted = true,
  controls = false,
  qualityMode = 'thumbnail', // 'thumbnail' or 'pdp'
  className = '',
  startTime = 0, // New prop for seeking to specific time
  onReady = null,
  onError = null,
  onPlay = null,
  onPause = null,
  onTimeUpdate = null,
  onEnd = null,
  style = {},
  consumerId = null, // Optional ID for transition coordination (e.g., 'thumbnail-hover', 'pdp-modal')
  ...props
}) => {
  // Generate a unique consumer ID if not provided, based on quality mode
  const effectiveConsumerId = consumerId || (qualityMode === 'pdp' ? 'pdp-modal' : 'thumbnail-hover');
  const containerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(muted);
  const [error, setError] = useState(null);

  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Individual memoized callbacks to prevent unnecessary re-renders
  const handleReady = useCallback(() => {
    setIsReady(true);
    setError(null);
    setIsLoading(false);

    // Calculate start time: use provided startTime or 20% of duration
    let finalStartTime = startTime;
    if (finalStartTime === 0) {
      // If no start time provided, use 20% of duration
      const videoElement = previewPlayerService.getVideoElement();
      if (videoElement && videoElement.duration && !isNaN(videoElement.duration)) {
        finalStartTime = Math.floor(videoElement.duration * 0.2);
      }
    }

    // Seek to calculated start time
    if (finalStartTime > 0) {
      previewPlayerService.seek(finalStartTime);
    }

    if (onReady) {
      onReady();
    }
  }, [onReady, startTime]);

  const handleError = useCallback(
    error => {
      console.error('GlobalPreviewPlayer: Error callback triggered:', error);
      setError(error);
      setIsReady(false);
      setIsLoading(false);
      if (onError) {
        onError(error);
      }
    },
    [onError],
  );

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
    if (onPlay) {
      onPlay();
    }
  }, [onPlay]);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
    if (onPause) {
      onPause();
    }
  }, [onPause]);

  const handleTimeUpdate = useCallback(
    time => {
      if (onTimeUpdate) {
        onTimeUpdate(time);
      }
    },
    [onTimeUpdate],
  );

  const handleEnd = useCallback(() => {
    setIsPlaying(false);
    if (onEnd) {
      onEnd();
    }
  }, [onEnd]);

  const handleMutedChange = useCallback(muted => {
    setIsMuted(muted);
  }, []);

  // Memoized callbacks object to prevent unnecessary re-renders
  const callbacks = useMemo(
    () => ({
      onReady: handleReady,
      onError: handleError,
      onPlay: handlePlay,
      onPause: handlePause,
      onTimeUpdate: handleTimeUpdate,
      onEnd: handleEnd,
      onMutedChange: handleMutedChange,
    }),
    [
      handleReady,
      handleError,
      handlePlay,
      handlePause,
      handleTimeUpdate,
      handleEnd,
      handleMutedChange,
    ],
  );

  // Set up callbacks once on mount and only when they change
  useEffect(() => {
    previewPlayerService.setCallbacks(callbacks);
  }, [callbacks]);

  // Set quality mode when it changes
  useEffect(() => {
    if (qualityMode && ['thumbnail', 'pdp'].includes(qualityMode)) {
      previewPlayerService.setQualityMode(qualityMode);
    }
  }, [qualityMode]);

  // Load URL when it changes (optimized)
  useEffect(() => {
    if (!hlsUrl) {
      setError('No HLS URL provided');
      return;
    }

    const loadUrl = async () => {
      try {
        setIsLoading(true);
        setError(null);

        await previewPlayerService.loadUrl(hlsUrl, { autoPlay, consumerId: effectiveConsumerId });
      } catch (error) {
        console.error('GlobalPreviewPlayer: Failed to load URL:', error);
        setError(error.message);
        setIsLoading(false);
      }
    };

    loadUrl();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hlsUrl, autoPlay, qualityMode]);

  // Initialize muted state from service
  useEffect(() => {
    setIsMuted(previewPlayerService.getMuted());
  }, []);

  // Attach video element to container when ready (optimized)
  useEffect(() => {
    if (isReady && containerRef.current) {
      const videoElement = previewPlayerService.getVideoElement();
      if (videoElement && !containerRef.current.contains(videoElement)) {
        // Remove from any existing parent
        if (videoElement.parentNode) {
          videoElement.parentNode.removeChild(videoElement);
        }

        // Add to our container
        containerRef.current.appendChild(videoElement);

        // Update video element properties
        videoElement.controls = controls;
        videoElement.className = 'preview-player-video';
      }
    }
  }, [isReady, controls]);

  // Cleanup on unmount (optimized)
  useEffect(() => {
    return () => {
      // Reset the service to clean up any pending operations
      // Pass consumer ID so the service can decide if reset should proceed
      // (e.g., skip reset if another consumer has already taken over)
      previewPlayerService.reset({ consumerId: qualityMode === 'pdp' ? 'pdp-modal' : 'thumbnail-hover' });
    };
  }, [qualityMode]);

  // Additional cleanup when URL changes
  useEffect(() => {
    return () => {
      // Clear any pending operations when URL changes
      // Only clear timeouts for this consumer, don't do a full reset
      if (previewPlayerService.loadTimeout) {
        clearTimeout(previewPlayerService.loadTimeout);
        previewPlayerService.loadTimeout = null;
      }
    };
  }, [hlsUrl]);

  // Memoized control methods
  const play = useCallback(() => {
    if (isReady) {
      previewPlayerService.play().catch(error => {
        console.warn('GlobalPreviewPlayer: Play failed:', error);
      });
    }
  }, [isReady]);

  const pause = useCallback(() => {
    previewPlayerService.pause();
  }, []);

  const toggleMute = useCallback(() => {
    const newMuted = !isMuted;
    previewPlayerService.setMuted(newMuted);
    setIsMuted(newMuted);
  }, [isMuted]);

  const seek = useCallback(
    time => {
      if (isReady) {
        previewPlayerService.seek(time);
      }
    },
    [isReady],
  );

  // Memoized container classes
  const containerClasses = useMemo(
    () =>
      [
        'preview-player',
        'global-preview-player',
        `preview-player-${qualityMode}`,
        className,
        isReady ? 'preview-player-ready' : 'preview-player-loading',
        error ? 'preview-player-error' : '',
      ]
        .filter(Boolean)
        .join(' '),
    [className, isReady, error, qualityMode],
  );

  // Memoized loading overlay
  const loadingOverlay = useMemo(() => {
    if (!isReady && !error && isLoading) {
      return (
        <div className="preview-player-loading-overlay">
          <div className="preview-player-spinner"></div>
        </div>
      );
    }
    return null;
  }, [isReady, error, isLoading]);

  // Memoized error overlay
  const errorOverlay = useMemo(() => {
    if (error) {
      return (
        <div className="preview-player-error-overlay">
          <div className="preview-player-error-message">
            <svg className="preview-player-error-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
            <span>Preview unavailable</span>
          </div>
        </div>
      );
    }
    return null;
  }, [error]);

  return (
    <div ref={containerRef} className={containerClasses} style={style} {...props}>
      {/* Video element will be attached here by the service */}
      {loadingOverlay}
      {errorOverlay}
    </div>
  );
};

export default GlobalPreviewPlayer;
